import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import SendToMobileRoundedIcon from "@mui/icons-material/SendToMobileRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import { useStyles } from "./styles";
import { difference } from "../../utils/date";
import { Notification as TNotification } from "../../types/notification";
import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Button from "@mui/material/Button";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  listNotificationById,
  readNotification,
} from "../../services/notifications";
import LinearProgress from "@mui/material/LinearProgress";
import getVideoIdYouTube from "../../utils/getVideoIdYouTube";

export function NotificationId() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const styles = useStyles();

  const notification = useQuery(
    ["notification", params.id],
    () => listNotificationById(params.id!),
    {
      enabled: !Boolean(location.state?.id),
    }
  );

  const readNotify = useMutation(
    async (id: string) => {
      const response = await readNotification(id);
      return response;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("notifications");
      },
    }
  );

  const data: TNotification = useMemo(() => {
    return location.state || notification.data;
  }, [location.state, notification.data]);

  const link = useMemo(() => {
    if (data?.freeze && data?.freeze?.link) {
      const videoId = getVideoIdYouTube(data?.freeze?.link);
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return "";
  }, [data?.freeze]);

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleGoToDashboard = useCallback(() => {
    navigate("/dashboard", { state: data.infos });
  }, [navigate, data]);

  const handleGoToWhatsApp = useCallback(() => {
    const payload = {
      ...data.infos,
      isGroup: data.notificationId === "115b381b89e5450490ffd05f52985eab",
    };
    navigate("/whatsapp", { state: payload });
  }, [navigate, data]);

  const handleRead = useCallback(async () => {
    await readNotify.mutateAsync(params.id!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  useEffect(() => {
    if (!Boolean(data?.read) && params.id) {
      handleRead();
    }
  }, [handleRead, data?.read, params.id]);

  return (
    <Container maxWidth="sm">
      <Stack spacing={2}>
        {notification.isLoading ? (
          <Box flexDirection="column" className={styles.root} padding={2}>
            <Typography variant="subtitle1">Buscando informações</Typography>
            <LinearProgress variant="indeterminate" sx={{ width: "50%" }} />
          </Box>
        ) : (
          <>
            <Box className={styles.root} padding={2}>
              <Stack
                width="100%"
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography>{data?.title}</Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ fontSize: "8pt", whiteSpace: "nowrap" }}
                  color="text.secondary"
                >
                  {difference(data?.sendingAt)}
                </Typography>
              </Stack>
            </Box>
            <Box flexDirection="column" className={styles.root} padding={2}>
              <Typography lineHeight="20pt" variant="body2">
                {data?.description}
              </Typography>
              {data.infos?.campaingId && (
                <Button
                  onClick={handleGoToDashboard}
                  size="small"
                  endIcon={<LocalShippingRoundedIcon />}
                >
                  Acompanhar campanha
                </Button>
              )}
              {data.infos?.instanceId && (
                <Button
                  onClick={handleGoToWhatsApp}
                  size="small"
                  endIcon={<SendToMobileRoundedIcon />}
                >
                  Acompanhar instância
                </Button>
              )}
            </Box>

            {link && (
              <Box style={{ padding: 0 }} className={styles.root} padding={2}>
                <iframe
                  className={styles.iframe}
                  src={link}
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </Box>
            )}

            <Box className={styles.root}>
              <Button size="small" fullWidth onClick={handleGoBack}>
                Voltar
              </Button>
            </Box>
          </>
        )}
      </Stack>
    </Container>
  );
}
